import { Grid } from '@mui/material';
import { RoutalDialog, VSpacer } from '@nx-smartmonkey/ui';
import { useEffect, useState } from 'react';

import { useUpdateSubscriptionPlan } from '../hooks/backoffice/subscriptions/useUpdateSubscriptionPlan';
import Button from '../react-components/Button';
import InputText from '../react-components/InputText';
import Text from '../react-components/Text';

interface SubscriptionExtraLimitsData {
  extra_vehicles: number;
  extra_supervisors: number;
  extra_projects: number;
  extra_users: number;
}

interface UpdateSubscriptionExtraLimitsDialogProps {
  userId: string;
  subscriptionData: SubscriptionExtraLimitsData;
  onClose: (modified?: boolean) => void;
}

export const UpdateSubscriptionExtraLimitsDialog = ({
  userId,
  subscriptionData,
  onClose,
}: UpdateSubscriptionExtraLimitsDialogProps) => {
  const { run: updateExtraLimits, isLoading, success: changePlanSuccess } = useUpdateSubscriptionPlan();
  const [extraLimits, setExtraLimits] = useState<SubscriptionExtraLimitsData>({
    extra_vehicles: subscriptionData.extra_vehicles,
    extra_supervisors: subscriptionData.extra_supervisors,
    extra_projects: subscriptionData.extra_projects,
    extra_users: subscriptionData.extra_users,
  });

  useEffect(() => {
    setExtraLimits({
      extra_vehicles: subscriptionData.extra_vehicles,
      extra_supervisors: subscriptionData.extra_supervisors,
      extra_projects: subscriptionData.extra_projects,
      extra_users: subscriptionData.extra_users,
    });
  }, [subscriptionData]);

  useEffect(() => {
    if (changePlanSuccess) {
      onClose(true);
    }
  }, [changePlanSuccess, onClose]);

  const isFormDisabled = () => {
    return (
      isLoading ||
      (subscriptionData.extra_supervisors === extraLimits.extra_supervisors &&
        subscriptionData.extra_projects === extraLimits.extra_projects &&
        subscriptionData.extra_users === extraLimits.extra_users &&
        subscriptionData.extra_vehicles === extraLimits.extra_vehicles)
    );
  };

  return (
    <RoutalDialog
      open
      onCloseDialog={() => onClose()}
      id="update-subscription-extra-limits-dialog"
      titleContent={<Text variant="h6" intlId={`user_profile_summary.change_plan.extra_limits`} />}
      dialogProps={{
        fullWidth: true,
        maxWidth: `sm`,
      }}
      footerContent={
        <>
          <Button variant="flat" intlId="cancel" onClick={() => onClose()} />
          <Button
            intlId="save"
            onClick={() => {
              updateExtraLimits({
                userId,
                extra_vehicles: extraLimits.extra_vehicles,
                extra_supervisors: extraLimits.extra_supervisors,
                extra_projects: extraLimits.extra_projects,
                extra_users: extraLimits.extra_users,
              });
            }}
            disabled={isFormDisabled()}
          />
        </>
      }
    >
      <Grid container style={{ padding: `16px` }} direction="column">
        <Grid item container direction="column">
          <Text variant="label" intlId="user_profile_summary.change_plan.extra_vehicles" />

          <VSpacer small />

          <InputText
            type="number"
            inputContainerStyles={{ width: `100%` }}
            placeholderIntlId="user_profile_summary.change_plan.extra_vehicles"
            fieldValue={extraLimits.extra_vehicles}
            onChange={(value: any) => {
              setExtraLimits({ ...extraLimits, extra_vehicles: value });
            }}
          />
        </Grid>

        <VSpacer medium />

        <Grid item container direction="column">
          <Text variant="label" intlId="user_profile_summary.change_plan.extra_supervisors" />

          <VSpacer small />

          <InputText
            type="number"
            inputContainerStyles={{ width: `100%` }}
            placeholderIntlId="user_profile_summary.change_plan.extra_supervisors"
            fieldValue={extraLimits.extra_supervisors}
            onChange={(value: any) => {
              setExtraLimits({ ...extraLimits, extra_supervisors: value });
            }}
          />
        </Grid>

        <VSpacer medium />

        <Grid item container direction="column">
          <Text variant="label" intlId="user_profile_summary.change_plan.extra_projects" />

          <VSpacer small />

          <InputText
            type="number"
            inputContainerStyles={{ width: `100%` }}
            placeholderIntlId="user_profile_summary.change_plan.extra_projects"
            fieldValue={extraLimits.extra_projects}
            onChange={(value: any) => {
              setExtraLimits({ ...extraLimits, extra_projects: value });
            }}
          />
        </Grid>

        <VSpacer medium />

        <Grid item container direction="column">
          <Text variant="label" intlId="user_profile_summary.change_plan.extra_users" />

          <VSpacer small />

          <InputText
            type="number"
            inputContainerStyles={{ width: `100%` }}
            placeholderIntlId="user_profile_summary.change_plan.extra_users"
            fieldValue={extraLimits.extra_users}
            onChange={(value: any) => {
              setExtraLimits({ ...extraLimits, extra_users: value });
            }}
          />
        </Grid>
      </Grid>
    </RoutalDialog>
  );
};
